import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { SignOut, Trash, EnvelopeSimple, Phone, Buildings, CurrencyEur, Gear, Tray, Check, Images, ChatCircleText, Plus, FloppyDisk, Question } from "@phosphor-icons/react";
import { api } from "../lib/api";

const inputCls = "w-full rounded-xl bg-[#050505] border border-white/10 px-4 py-3 text-white text-sm focus:border-blue-500 focus:outline-none transition-colors";

const emptyPortfolio = { title_it: "", title_en: "", category: "", desc_it: "", desc_en: "", image_url: "", featured: false, order: 0 };
const emptyTestimonial = { name: "", role_it: "", role_en: "", text_it: "", text_en: "", image_url: "", order: 0 };
const emptyFaq = { q_it: "", q_en: "", a_it: "", a_en: "", order: 0 };

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("contacts");
  const [contacts, setContacts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [portfolio, setPortfolio] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faq, setFaq] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [c, s, p, t, f] = await Promise.all([
        api.get("/admin/contacts"), api.get("/settings"), api.get("/portfolio"), api.get("/testimonials"), api.get("/faq"),
      ]);
      setContacts(c.data); setSettings(s.data); setPortfolio(p.data); setTestimonials(t.data); setFaq(f.data);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.removeItem("af_token"); nav("/admin/login"); }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!localStorage.getItem("af_token")) { nav("/admin/login"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => { localStorage.removeItem("af_token"); nav("/admin/login"); };

  // Contacts
  const del = async (id) => { await api.delete(`/admin/contacts/${id}`); setContacts((c) => c.filter((x) => x.id !== id)); toast.success("Contatto eliminato"); };
  const markRead = async (id) => { await api.patch(`/admin/contacts/${id}`, { status: "read" }); setContacts((c) => c.map((x) => (x.id === id ? { ...x, status: "read" } : x))); };

  // Settings
  const saveSettings = async (e) => { e.preventDefault(); await api.put("/admin/settings", settings); toast.success("Impostazioni salvate"); };
  const setS = (k) => (e) => setSettings((s) => ({ ...s, [k]: e.target.value }));

  // Portfolio
  const updPf = (i, k, v) => setPortfolio((arr) => arr.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const savePf = async (item) => { const { data } = item.id ? await api.put(`/admin/portfolio/${item.id}`, item) : await api.post(`/admin/portfolio`, item); toast.success("Progetto salvato"); load(); return data; };
  const delPf = async (item, i) => { if (item.id) await api.delete(`/admin/portfolio/${item.id}`); setPortfolio((a) => a.filter((_, idx) => idx !== i)); toast.success("Progetto eliminato"); };
  const addPf = () => setPortfolio((a) => [...a, { ...emptyPortfolio, order: a.length }]);

  // Testimonials
  const updTs = (i, k, v) => setTestimonials((arr) => arr.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const saveTs = async (item) => { item.id ? await api.put(`/admin/testimonials/${item.id}`, item) : await api.post(`/admin/testimonials`, item); toast.success("Testimonianza salvata"); load(); };
  const delTs = async (item, i) => { if (item.id) await api.delete(`/admin/testimonials/${item.id}`); setTestimonials((a) => a.filter((_, idx) => idx !== i)); toast.success("Testimonianza eliminata"); };
  const addTs = () => setTestimonials((a) => [...a, { ...emptyTestimonial, order: a.length }]);

  // FAQ
  const updFq = (i, k, v) => setFaq((arr) => arr.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const saveFq = async (item) => { item.id ? await api.put(`/admin/faq/${item.id}`, item) : await api.post(`/admin/faq`, item); toast.success("FAQ salvata"); load(); };
  const delFq = async (item, i) => { if (item.id) await api.delete(`/admin/faq/${item.id}`); setFaq((a) => a.filter((_, idx) => idx !== i)); toast.success("FAQ eliminata"); };
  const addFq = () => setFaq((a) => [...a, { ...emptyFaq, order: a.length }]);

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-zinc-500">Caricamento...</div>;

  const newCount = contacts.filter((c) => c.status === "new").length;
  const TabBtn = ({ id, icon: Icon, label, badge }) => (
    <button data-testid={`tab-${id}`} onClick={() => setTab(id)}
      className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${tab === id ? "bg-white text-black" : "border border-white/15 text-zinc-400 hover:text-white"}`}>
      <Icon size={16} /> {label} {badge > 0 && <span className="ml-1 bg-blue-500 text-white text-xs rounded-full px-2 py-0.5">{badge}</span>}
    </button>
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="noise-overlay" />
      <header className="border-b border-white/10 px-6 md:px-10 h-16 flex items-center justify-between sticky top-0 bg-[#050505]/90 backdrop-blur-xl z-10">
        <div className="flex items-center gap-2">
          <img src="/appforge-logo.png" alt="AppForge" className="w-7 h-7 rounded-lg object-cover" />
          <span className="font-heading font-bold">AppForge <span className="text-blue-500">Admin</span></span>
        </div>
        <button data-testid="logout-btn" onClick={logout} className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors">
          <SignOut size={18} /> Esci
        </button>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-wrap gap-2 mb-8">
          <TabBtn id="contacts" icon={Tray} label="Contatti" badge={newCount} />
          <TabBtn id="portfolio" icon={Images} label="Portfolio" badge={0} />
          <TabBtn id="testimonials" icon={ChatCircleText} label="Testimonianze" badge={0} />
          <TabBtn id="faq" icon={Question} label="FAQ" badge={0} />
          <TabBtn id="settings" icon={Gear} label="Prezzi & Testi" badge={0} />
        </div>

        {tab === "contacts" && (
          <div data-testid="contacts-panel">
            <h1 className="font-heading font-bold text-2xl mb-6">Richieste ricevute ({contacts.length})</h1>
            {contacts.length === 0 && <p className="text-zinc-500">Nessuna richiesta al momento.</p>}
            <div className="grid gap-4">
              {contacts.map((c) => (
                <div key={c.id} data-testid={`contact-row-${c.id}`} className={`rounded-2xl border p-6 ${c.status === "new" ? "border-blue-500/40 bg-blue-500/5" : "border-white/10 bg-[#0A0A0A]"}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-lg">{c.nome} {c.cognome}</h3>
                        {c.status === "new" && <span className="text-xs bg-blue-500 text-white rounded-full px-2 py-0.5">Nuovo</span>}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-zinc-400">
                        <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 hover:text-white"><EnvelopeSimple size={14} />{c.email}</a>
                        {c.telefono && <a href={`tel:${c.telefono}`} className="inline-flex items-center gap-1.5 hover:text-white"><Phone size={14} />{c.telefono}</a>}
                        {c.azienda && <span className="inline-flex items-center gap-1.5"><Buildings size={14} />{c.azienda}</span>}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {c.status === "new" && <button data-testid={`mark-read-${c.id}`} onClick={() => markRead(c.id)} aria-label="Segna come letto" className="p-2 rounded-lg border border-white/10 text-zinc-400 hover:text-blue-400 hover:border-blue-500 transition-colors" title="Segna come letto"><Check size={16} /></button>}
                      <button data-testid={`delete-${c.id}`} onClick={() => del(c.id)} aria-label="Elimina contatto" className="p-2 rounded-lg border border-white/10 text-zinc-400 hover:text-red-400 hover:border-red-500 transition-colors"><Trash size={16} /></button>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    {c.servizio && <span className="rounded-full bg-white/5 border border-white/10 px-3 py-1 text-zinc-300">{c.servizio}</span>}
                    {c.budget && <span className="rounded-full bg-white/5 border border-white/10 px-3 py-1 text-zinc-300">{c.budget}</span>}
                    <span className="rounded-full px-3 py-1 text-zinc-600">{new Date(c.created_at).toLocaleString("it-IT")}</span>
                  </div>
                  {c.descrizione && <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{c.descrizione}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "portfolio" && (
          <div data-testid="portfolio-panel">
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-heading font-bold text-2xl">Portfolio ({portfolio.length})</h1>
              <button data-testid="add-portfolio" onClick={addPf} className="inline-flex items-center gap-2 rounded-full bg-blue-500 text-white text-sm font-semibold px-4 py-2.5 hover:bg-blue-400 transition-colors"><Plus size={16} /> Aggiungi</button>
            </div>
            <div className="grid gap-5">
              {portfolio.map((p, i) => (
                <div key={p.id || `new-${i}`} data-testid={`pf-card-${i}`} className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-5">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input className={inputCls} placeholder="Titolo (IT)" value={p.title_it} onChange={(e) => updPf(i, "title_it", e.target.value)} />
                    <input className={inputCls} placeholder="Title (EN)" value={p.title_en} onChange={(e) => updPf(i, "title_en", e.target.value)} />
                    <input className={inputCls} placeholder="Categoria" value={p.category} onChange={(e) => updPf(i, "category", e.target.value)} />
                    <input className={inputCls} placeholder="URL immagine" value={p.image_url} onChange={(e) => updPf(i, "image_url", e.target.value)} />
                    <textarea rows={2} className={inputCls + " resize-none"} placeholder="Descrizione (IT)" value={p.desc_it} onChange={(e) => updPf(i, "desc_it", e.target.value)} />
                    <textarea rows={2} className={inputCls + " resize-none"} placeholder="Description (EN)" value={p.desc_en} onChange={(e) => updPf(i, "desc_en", e.target.value)} />
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-4">
                    <label className="inline-flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" checked={!!p.featured} onChange={(e) => updPf(i, "featured", e.target.checked)} /> In evidenza</label>
                    <label className="inline-flex items-center gap-2 text-sm text-zinc-400">Ordine <input type="number" className="w-20 rounded-lg bg-[#050505] border border-white/10 px-2 py-1 text-white" value={p.order} onChange={(e) => updPf(i, "order", parseInt(e.target.value) || 0)} /></label>
                    <div className="ml-auto flex gap-2">
                      <button data-testid={`pf-save-${i}`} onClick={() => savePf(p)} className="inline-flex items-center gap-2 rounded-full bg-white text-black text-sm font-semibold px-4 py-2 hover:bg-blue-500 hover:text-white transition-colors"><FloppyDisk size={15} /> Salva</button>
                      <button data-testid={`pf-del-${i}`} onClick={() => delPf(p, i)} aria-label="Elimina progetto" className="p-2 rounded-lg border border-white/10 text-zinc-400 hover:text-red-400 hover:border-red-500 transition-colors"><Trash size={16} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "testimonials" && (
          <div data-testid="testimonials-panel">
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-heading font-bold text-2xl">Testimonianze ({testimonials.length})</h1>
              <button data-testid="add-testimonial" onClick={addTs} className="inline-flex items-center gap-2 rounded-full bg-blue-500 text-white text-sm font-semibold px-4 py-2.5 hover:bg-blue-400 transition-colors"><Plus size={16} /> Aggiungi</button>
            </div>
            <div className="grid gap-5">
              {testimonials.map((tst, i) => (
                <div key={tst.id || `new-${i}`} data-testid={`ts-card-${i}`} className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-5">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input className={inputCls} placeholder="Nome" value={tst.name} onChange={(e) => updTs(i, "name", e.target.value)} />
                    <input className={inputCls} placeholder="URL foto" value={tst.image_url} onChange={(e) => updTs(i, "image_url", e.target.value)} />
                    <input className={inputCls} placeholder="Ruolo (IT)" value={tst.role_it} onChange={(e) => updTs(i, "role_it", e.target.value)} />
                    <input className={inputCls} placeholder="Role (EN)" value={tst.role_en} onChange={(e) => updTs(i, "role_en", e.target.value)} />
                    <textarea rows={2} className={inputCls + " resize-none"} placeholder="Testo (IT)" value={tst.text_it} onChange={(e) => updTs(i, "text_it", e.target.value)} />
                    <textarea rows={2} className={inputCls + " resize-none"} placeholder="Text (EN)" value={tst.text_en} onChange={(e) => updTs(i, "text_en", e.target.value)} />
                  </div>
                  <div className="mt-3 flex items-center gap-4">
                    <label className="inline-flex items-center gap-2 text-sm text-zinc-400">Ordine <input type="number" className="w-20 rounded-lg bg-[#050505] border border-white/10 px-2 py-1 text-white" value={tst.order} onChange={(e) => updTs(i, "order", parseInt(e.target.value) || 0)} /></label>
                    <div className="ml-auto flex gap-2">
                      <button data-testid={`ts-save-${i}`} onClick={() => saveTs(tst)} className="inline-flex items-center gap-2 rounded-full bg-white text-black text-sm font-semibold px-4 py-2 hover:bg-blue-500 hover:text-white transition-colors"><FloppyDisk size={15} /> Salva</button>
                      <button data-testid={`ts-del-${i}`} onClick={() => delTs(tst, i)} aria-label="Elimina testimonianza" className="p-2 rounded-lg border border-white/10 text-zinc-400 hover:text-red-400 hover:border-red-500 transition-colors"><Trash size={16} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "faq" && (
          <div data-testid="faq-panel">
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-heading font-bold text-2xl">FAQ ({faq.length})</h1>
              <button data-testid="add-faq" onClick={addFq} className="inline-flex items-center gap-2 rounded-full bg-blue-500 text-white text-sm font-semibold px-4 py-2.5 hover:bg-blue-400 transition-colors"><Plus size={16} /> Aggiungi</button>
            </div>
            <div className="grid gap-5">
              {faq.map((f, i) => (
                <div key={f.id || `new-${i}`} data-testid={`faq-card-${i}`} className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-5">
                  <div className="grid gap-3">
                    <input className={inputCls} placeholder="Domanda (IT)" value={f.q_it} onChange={(e) => updFq(i, "q_it", e.target.value)} />
                    <input className={inputCls} placeholder="Question (EN)" value={f.q_en} onChange={(e) => updFq(i, "q_en", e.target.value)} />
                    <textarea rows={2} className={inputCls + " resize-none"} placeholder="Risposta (IT)" value={f.a_it} onChange={(e) => updFq(i, "a_it", e.target.value)} />
                    <textarea rows={2} className={inputCls + " resize-none"} placeholder="Answer (EN)" value={f.a_en} onChange={(e) => updFq(i, "a_en", e.target.value)} />
                  </div>
                  <div className="mt-3 flex items-center gap-4">
                    <label className="inline-flex items-center gap-2 text-sm text-zinc-400">Ordine <input type="number" className="w-20 rounded-lg bg-[#050505] border border-white/10 px-2 py-1 text-white" value={f.order} onChange={(e) => updFq(i, "order", parseInt(e.target.value) || 0)} /></label>
                    <div className="ml-auto flex gap-2">
                      <button data-testid={`fq-save-${i}`} onClick={() => saveFq(f)} className="inline-flex items-center gap-2 rounded-full bg-white text-black text-sm font-semibold px-4 py-2 hover:bg-blue-500 hover:text-white transition-colors"><FloppyDisk size={15} /> Salva</button>
                      <button data-testid={`fq-del-${i}`} onClick={() => delFq(f, i)} aria-label="Elimina FAQ" className="p-2 rounded-lg border border-white/10 text-zinc-400 hover:text-red-400 hover:border-red-500 transition-colors"><Trash size={16} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "settings" && settings && (
          <form onSubmit={saveSettings} data-testid="settings-panel" className="max-w-xl">
            <h1 className="font-heading font-bold text-2xl mb-6">Prezzi & Testi</h1>
            <div className="space-y-5 rounded-2xl border border-white/10 bg-[#0A0A0A] p-6">
              <div className="grid sm:grid-cols-3 gap-4">
                <label className="text-sm text-zinc-400">App (una tantum €)
                  <input data-testid="set-app-oneoff" className={inputCls + " mt-1"} value={settings.app_price_oneoff} onChange={setS("app_price_oneoff")} /></label>
                <label className="text-sm text-zinc-400">App (mensile €)
                  <input data-testid="set-app-monthly" className={inputCls + " mt-1"} value={settings.app_price_monthly} onChange={setS("app_price_monthly")} /></label>
                <label className="text-sm text-zinc-400">Landing (€)
                  <input data-testid="set-landing" className={inputCls + " mt-1"} value={settings.landing_price} onChange={setS("landing_price")} /></label>
              </div>
              <label className="block text-sm text-zinc-400">Hero title (IT)
                <input data-testid="set-hero-it" className={inputCls + " mt-1"} value={settings.hero_title_it} onChange={setS("hero_title_it")} /></label>
              <label className="block text-sm text-zinc-400">Hero title (EN)
                <input data-testid="set-hero-en" className={inputCls + " mt-1"} value={settings.hero_title_en} onChange={setS("hero_title_en")} /></label>
              <button data-testid="save-settings" type="submit" className="inline-flex items-center gap-2 rounded-full bg-blue-500 text-white font-semibold px-6 py-3 hover:bg-blue-400 transition-colors">
                <CurrencyEur size={16} /> Salva impostazioni
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
